// Components
import { CircularProgress, Typography } from '@mui/material'
import Alert from '@mui/material/Alert'
import LandingIcon from './LandingIcon/LandingIcon'

// Logic
import { useMainIconLandingLogic } from './MainIconLanding.logic'

// Styles
import classNames from 'classnames'
import './MainIconLanding.scss'

// Other
import { Helmet } from 'react-helmet'
import { CustomModal } from 'components/molecules/Modal/Modal'
import { MainIconLandingProps } from './MainIconLanding.types'
import { FC } from 'react'
import { SelectOldExplantsModal } from './SelectOldExplantsModal/SelectOldExplantsModal'
import { BatchDispositionModal } from 'components/BatchDispositionModal/BatchDispositionModal'
import { useNavigate } from 'react-router-dom'
import { AssignCardModal } from 'views/PreferenceCards/AssignCardModal/AssignCardModal'
import { useUser } from 'app/User'

const MainIconLanding: FC<MainIconLandingProps> = ({
  surgery,
  derivedSurgeryStatus,
  setOnMainIconLanding,
}) => {
  const {
    icons,
    loading,
    isMobile,
    oldImplants,
    twoColumnsClass,
    loadingOldImplants,
    selectedOldImplants,
    loadingBatchAddAssets,
    isAssignCardModalOpen,
    isDispositionModalOpen,
    selectOldExplantsModalOpen,
    showConsumablesAutopopulate,
    handleClick,
    populateConsumables,
    handleAutoExplantSave,
    setSelectedOldImplants,
    handleSaveUploadedMedia,
    setIsDispositionModalOpen,
    setSelectOldExplantsModalOpen,
    handleCloseAssignCardModal,
  } = useMainIconLandingLogic({ surgery, derivedSurgeryStatus })
  const navigate = useNavigate()
  const { isRep } = useUser()

  const isPreApproval = derivedSurgeryStatus === 'PRE_APPROVAL'

  return (
    <div
      className={classNames('main-icon-landing_wrapper', {
        'is-mobile': isMobile,
      })}
    >
      {loadingOldImplants && (
        <div className="loader">
          <CircularProgress />
        </div>
      )}

      <Helmet>
        <title>Home</title>
      </Helmet>

      <Typography variant="h1" align="center">
        {isRep ? 'RN' : 'Vendor'} Documentation
      </Typography>

      {isPreApproval && (
        <Alert
          severity="warning"
          sx={{
            marginX: 2,
            '& a': {
              color: 'inherit',
              textDecoration: 'underline',
            },
            maxWidth: '800px',
          }}
        >
          This procedure is in a <strong>pre-approval</strong> state. Changes
          cannot be made until approval is completed or canceled. Please proceed
          to the{' '}
          <button
            className="pre-approval-link"
            onClick={() => {
              setOnMainIconLanding?.(false)
              navigate(`/surgeries/${surgery?._id}/list`)
            }}
          >
            Documented List
          </button>{' '}
          to complete the approval process.
        </Alert>
      )}

      <div className={`icons-container ${twoColumnsClass}`}>
        {/* Convert icons object to array and filter visible ones */}
        {(() => {
          const visibleIcons = Object.entries(icons).filter(
            ([, { isVisible }]) => isVisible !== false
          )

          const topRowIcons = visibleIcons.slice(0, 3)
          const middleRowIcon = visibleIcons.slice(3, 4)
          const bottomRowIcons = visibleIcons.slice(4, 7)

          return (
            <>
              {/* Top Row - 3 icons */}
              <div className="icons-row icons-row-top">
                {topRowIcons.map(
                  ([key, { IconComponent, label, dataTestId, isDisabled }]) => (
                    <LandingIcon
                      key={key}
                      isMobile={isMobile}
                      onClick={() => handleClick(label)}
                      isDisabled={isDisabled}
                      icon={IconComponent}
                      label={label}
                      dataTestId={dataTestId}
                    />
                  )
                )}
              </div>

              {/* Middle Row - 1 icon */}
              {middleRowIcon.length > 0 && (
                <div className="icons-row icons-row-middle">
                  {middleRowIcon.map(
                    ([
                      key,
                      { IconComponent, label, dataTestId, isDisabled },
                    ]) => (
                      <LandingIcon
                        key={key}
                        isMobile={isMobile}
                        onClick={() => handleClick(label)}
                        isDisabled={isDisabled}
                        icon={IconComponent}
                        label={label}
                        dataTestId={dataTestId}
                      />
                    )
                  )}
                </div>
              )}

              {/* Bottom Row - 3 icons */}
              {bottomRowIcons.length > 0 && (
                <div className="icons-row icons-row-bottom">
                  {bottomRowIcons.map(
                    ([
                      key,
                      { IconComponent, label, dataTestId, isDisabled },
                    ]) => (
                      <LandingIcon
                        key={key}
                        isMobile={isMobile}
                        onClick={() => handleClick(label)}
                        isDisabled={isDisabled}
                        icon={IconComponent}
                        label={label}
                        dataTestId={dataTestId}
                      />
                    )
                  )}
                </div>
              )}
            </>
          )
        })()}
      </div>

      {showConsumablesAutopopulate && (
        <CustomModal
          header="Consumables Autopopulate"
          fitContentWidth={!isMobile}
          open={showConsumablesAutopopulate}
          isLoadingState={loading}
          primaryButtonText="YES"
          secondaryButtonText="NO"
          onCloseTakeSecondaryButtonAction={false}
          secondaryButtonAction={() => populateConsumables(false)}
          handleOnSubmit={() => populateConsumables(true)}
          icon={<></>}
        >
          <Typography>
            A preference card was found for this surgery, Would you like to add
            all the consumables from it?
          </Typography>
        </CustomModal>
      )}

      {selectOldExplantsModalOpen && (
        <SelectOldExplantsModal
          isLoading={loadingOldImplants}
          oldImplants={oldImplants}
          open={selectOldExplantsModalOpen}
          onClose={() => setSelectOldExplantsModalOpen(false)}
          onSetIsDispositionModalOpen={setIsDispositionModalOpen}
          onSetSelectedOldImplants={setSelectedOldImplants}
        />
      )}

      {isDispositionModalOpen && (
        <BatchDispositionModal
          isAutoExplant
          isOpen={isDispositionModalOpen}
          scan={selectedOldImplants[0]}
          selectedScans={selectedOldImplants}
          assetType="explanted"
          dispositionStatus="EXPLANTED"
          isManualAddition={false}
          activeTray={surgery?.activeAssetTray || undefined}
          assetTrays={surgery?.assetTrays || []}
          onAutoExplantSave={handleAutoExplantSave}
          onSaveMedia={handleSaveUploadedMedia}
          onClose={() => {
            setIsDispositionModalOpen(false)
          }}
          isSubmitting={loadingBatchAddAssets}
          setIsTrayScannerOpen={() => {}}
        />
      )}

      {isAssignCardModalOpen && (
        <AssignCardModal
          open={isAssignCardModalOpen}
          onClose={handleCloseAssignCardModal}
          onCreateNew={() => {
            handleCloseAssignCardModal()
            navigate(`/preference-cards/new`, {
              state: {
                surgeonId: surgery.attendingSurgeon?.provider._id,
                procedureType: surgery.procedures[0]?.description,
              },
            })
          }}
          data={{
            surgeryId: surgery._id,
            surgeonId: surgery.visit?.attendingProvider?._id || '',
            patientMRN: surgery.patient.idMR,
            patientLastName: surgery.patient.lastName,
            patientFirstName: surgery.patient.firstName,
            surgeonLastName: surgery.visit?.attendingProvider?.lastName || '',
            surgeonFirstName: surgery.visit?.attendingProvider?.firstName || '',
            visitDate: surgery.visit?.visitDateTime || '',
            totalCost: surgery.totalSurgeryCost || 0,
            surgicalCaseStatus: surgery.status.name,
            procedures: surgery.procedures.map((procedure) => ({
              description: procedure.description || '',
              dateTime: procedure.dateTime,
            })),
            location: surgery.location?.room || '',
            preferenceCardId: surgery.preferenceCardId || '',
            preferenceCardName: '',
          }}
        />
      )}
    </div>
  )
}

export default MainIconLanding
